const configuredApiUrl = process.env.REACT_APP_API_URL;
const API_URL = configuredApiUrl
  ? configuredApiUrl.replace(/\/$/, '')
  // : `${window.location.protocol}//${window.location.hostname}:5001`;
  : `http://192.168.1.28:5001`;

class AudioService {
  constructor() {
    this.audioContext = null;
    this.masterGain = null;
    this.cachedBuffers = {};
    this.activeSources = {};
    this.ambientAudioSource = null;
    this.currentlyPlayingExclusiveLoop = null;
    this.ambientSoundPad = "999";
    this.isMuted = false;
    this.isLoadingScreen = true;
    this.pendingAmbientPlay = false;
    this._pendingPlay = null;
    this.elementToPadMapping = {};
    this.activeTimeouts = new Set();
    this._soundData = [];
    this.generation = 0; // tracks navigation/stop generations

    this._initContext();

    const onGesture = () => {
      if (this.audioContext && this.audioContext.state === 'suspended') {
        console.log('[AudioService] Activity detected, attempting to resume AudioContext');
        this.audioContext.resume().then(() => {
          if (this.audioContext.state === 'running') {
            console.log('[AudioService] AudioContext successfully resumed.');
            this._runPendingPlay();
            this._removeGestureListeners();
          }
        }).catch((err) => {
          console.warn('[AudioService] Resume failed:', err);
        });
      } else if (this.audioContext && this.audioContext.state === 'running') {
        this._runPendingPlay();
        this._removeGestureListeners();
      }
    };

    this._removeGestureListeners = () => {
      const events = ['click', 'touchstart', 'touchend', 'mousedown', 'keydown', 'pointerdown', 'wheel', 'scroll', 'mousemove'];
      events.forEach(e => document.removeEventListener(e, onGesture));
      window.removeEventListener('scroll', onGesture);
    };

    this._gestureHandler = onGesture;
    const events = ['click', 'touchstart', 'touchend', 'mousedown', 'keydown', 'pointerdown', 'wheel', 'scroll', 'mousemove'];
    events.forEach(e => document.addEventListener(e, onGesture, { passive: true }));
    window.addEventListener('scroll', onGesture, { passive: true });

    // Auto-setup HTML audio element for instant context unlock
    this._setupHtmlAudioUnlock();
  }

  _setupHtmlAudioUnlock() {
    const primer = document.getElementById('audio-primer');
    if (!primer) return;

    const attemptForcePlay = async () => {
      console.log('[AudioService] Attempting aggressive force-play unlock');
      this._initContext();
      if (!this.audioContext) return;

      try {
        // Try playing unmuted first
        primer.muted = false;
        await primer.play();
        await this.audioContext.resume();
        console.log('[AudioService] SUCCESS: Force-played unmuted audio');
      } catch (e) {
        console.log('[AudioService] Unmuted force-play blocked, falling back to muted-start');
        // Fallback: Start muted (usually allowed), then unmute on first activity
        primer.muted = true;
        try {
          await primer.play();
          console.log('[AudioService] Muted primer started');
        } catch (err) {
          console.log('[AudioService] Muted primer also blocked');
        }
      }
    };

    // Run once immediately
    attemptForcePlay();

    // Aggressive unblockers: any movement or activity triggers an unmute attempt
    const unblock = async () => {
      if (this.audioContext && this.audioContext.state === 'running' && !primer.muted) {
        cleanup();
        return;
      }

      console.log('[AudioService] Activity detected, forcing audio unblock');
      primer.muted = false;
      try {
        await primer.play();
        if (this.audioContext) await this.audioContext.resume();
        console.log('[AudioService] Audio successfully unblocked via activity');
        this._runPendingPlay();
        cleanup();
      } catch (e) {
        // Still blocked, keep listeners
      }
    };

    const cleanup = () => {
      window.removeEventListener('mousemove', unblock);
      window.removeEventListener('scroll', unblock);
      window.removeEventListener('mousedown', unblock);
      window.removeEventListener('touchstart', unblock);
    };

    window.addEventListener('mousemove', unblock);
    window.addEventListener('scroll', unblock);
    window.addEventListener('mousedown', unblock);
    window.addEventListener('touchstart', unblock);
  }

  _initContext() {
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.masterGain = this.audioContext.createGain();
        this.masterGain.connect(this.audioContext.destination);
      }
    } catch (e) {
      console.warn('[AudioService] Could not initialize AudioContext yet:', e);
    }
  }

  async unlockAudioContextWithMediaElement(mediaElement) {
    if (!mediaElement) return false;
    this._initContext();
    if (!this.audioContext) return false;

    try {
      const source = this.audioContext.createMediaElementSource(mediaElement);
      source.connect(this.masterGain || this.audioContext.destination);

      // Attempt to play the muted audio (may succeed for muted media)
      try {
        await mediaElement.play();
      } catch (e) {
        console.log('[AudioService] Muted primer play failed (expected):', e.message);
      }

      // Do NOT attempt to resume here - it requires a user gesture and will fail
      // The gesture listeners in the constructor will handle resuming when user interacts
      // Queue pending playback so it auto-starts when gesture resumes the context
      if (!this.isMuted && !this.isLoadingScreen) {
        console.log('[AudioService] Queueing ambient sound for playback after gesture');
        this._pendingPlay = { type: 'ambient', volume: 1.0, fadeInDuration: 1500 };
      }

      source.disconnect();
      return false; // Context still suspended, pending user gesture
    } catch (e) {
      console.warn('[AudioService] Could not setup audio unlock:', e);
      return false;
    }
  }

  _runPendingPlay() {
    if (!this._pendingPlay || this.isMuted || this.isLoadingScreen) return;
    const pending = this._pendingPlay;
    this._pendingPlay = null;
    if (pending.type === 'ambient') {
      this.playAmbientSound(pending.volume, pending.fadeInDuration).catch(() => { });
    } else if (pending.type === 'pad') {
      this.playPadSound(pending.padNumber, pending.options).catch(() => { });
    }
  }

  async _ensureContextRunning() {
    if (!this.audioContext) {
      this._initContext();
    }

    if (this.audioContext.state !== 'running') {
      try {
        await this.audioContext.resume();
      } catch (e) {
        console.error('[AudioService] Failed to resume context:', e);
        return false;
      }
    }

    return this.audioContext.state === 'running';
  }

  init(soundData) {
    if (!soundData || soundData.length === 0) {
      console.warn('[AudioService] init called with empty data!');
      return;
    }
    this._soundData = soundData;
    this.buildElementToPadMapping(soundData);
    if (this.audioContext && this.audioContext.state === 'running') {
      this.preloadCommonSounds();
    }
  }

  _normalizeElementId(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '');
  }

  buildElementToPadMapping(soundData) {
    if (!soundData || !Array.isArray(soundData)) return;
    this.elementToPadMapping = {};

    const ID_COLS = ['id', 'Id', 'ID', 'name', 'Name', 'pollutant', 'Pollutant',
      'element', 'Element', 'Pollutantname_split', 'unique_id'];
    const PAD_COLS = ['Number', 'number', 'Pad', 'pad', 'PadNumber', 'padNumber',
      'SoundNumber', 'soundNumber', 'sound_number', 'Num'];

    soundData.forEach(row => {
      let idVal = '';
      for (const col of ID_COLS) {
        if (row[col] && String(row[col]).trim()) { idVal = String(row[col]).trim(); break; }
      }
      let padVal = '';
      for (const col of PAD_COLS) {
        if (row[col] && String(row[col]).trim()) { padVal = String(row[col]).trim(); break; }
      }

      if (idVal && padVal) {
        const rawId = String(idVal).toLowerCase().trim();
        const normalizedId = this._normalizeElementId(idVal);
        this.elementToPadMapping[rawId] = padVal;
        this.elementToPadMapping[normalizedId] = padVal;
      }
    });

    const variants = {
      'aluminium': 'aluminium', 'thallium': 'thallium',
      'estrogen-pills': 'estrogen', 'organicmatter': 'organicmatter', 'crude': 'crudeoil'
    };
    Object.entries(variants).forEach(([v, s]) => {
      const normalizedSource = this._normalizeElementId(s);
      if (this.elementToPadMapping[normalizedSource]) {
        this.elementToPadMapping[this._normalizeElementId(v)] = this.elementToPadMapping[normalizedSource];
      }
    });

    if (!this.elementToPadMapping['crude'] && this.elementToPadMapping['crudeoilgrease']) {
      this.elementToPadMapping['crude'] = this.elementToPadMapping['crudeoilgrease'];
    }
  }

  addMappings(extraMap) {
    if (!extraMap || typeof extraMap !== 'object') return;
    Object.entries(extraMap).forEach(([id, pad]) => {
      const rawKey = String(id).trim().toLowerCase();
      const normalizedKey = this._normalizeElementId(id);
      if (!rawKey || !pad) return;
      const padStr = String(pad);
      const aliases = new Set([
        rawKey,
        normalizedKey,
        rawKey.replace(/-/g, ''),
        rawKey.replace(/\s+/g, ''),
        rawKey.replace(/[^a-z0-9]+/g, ''),
      ]);

      if ([rawKey, normalizedKey].some(k => /crudeoil/.test(k) || /crudeoilgrease/.test(k))) {
        aliases.add('crude');
      }

      aliases.forEach(alias => {
        if (alias) {
          this.elementToPadMapping[alias] = padStr;
        }
      });
    });
  }

  getMappingDebug() {
    return { ...this.elementToPadMapping };
  }

  _createAudioSource(audioBuffer, volume, fadeIn, fadeInDuration) {
    if (this.isMuted) return { source: null, gain: null };
    const source = this.audioContext.createBufferSource();
    const gain = this.audioContext.createGain();
    source.buffer = audioBuffer;
    source.connect(gain).connect(this.masterGain);
    source.start(0);
    if (fadeIn) {
      const now = this.audioContext.currentTime;
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.exponentialRampToValueAtTime(volume, now + fadeInDuration / 1000);
    } else {
      gain.gain.setValueAtTime(volume, this.audioContext.currentTime);
    }
    return { source, gain };
  }

  _scheduleOverlappingLoop(loopController, startGen) {
    if (loopController.stopped || this.isMuted || this.generation !== startGen) return;
    const { audioBuffer, settings, padNumber } = loopController;
    const soundInfo = this._soundData.find(item => String(item.Number) === String(padNumber));
    const overlapTime = soundInfo?.overlapDuration ? parseFloat(soundInfo.overlapDuration) : 1.0;
    const bufferDuration = audioBuffer.duration;

    if (overlapTime >= bufferDuration) {
      const tid = setTimeout(() => {
        if (!loopController.stopped && !this.isMuted && this.generation === startGen) {
          this._scheduleOverlappingLoop(loopController, startGen);
        }
        this.activeTimeouts.delete(tid);
      }, bufferDuration * 1000);
      this.activeTimeouts.add(tid);
      return;
    }

    const tid = setTimeout(() => {
      if (loopController.stopped || this.isMuted || this.generation !== startGen) return;
      try {
        const { source: ns, gain: ng } = this._createAudioSource(audioBuffer, settings.volume, settings.fadeIn, settings.fadeInDuration);
        if (!ns) return;
        const old = loopController.source;
        loopController.source = ns;
        loopController.gain = ng;
        const ctid = setTimeout(() => { try { old.stop(0); } catch (e) { } this.activeTimeouts.delete(ctid); }, overlapTime * 1000);
        this.activeTimeouts.add(ctid);
        this._scheduleOverlappingLoop(loopController, startGen);
      } catch (e) { }
      this.activeTimeouts.delete(tid);
    }, (bufferDuration - overlapTime) * 1000);
    this.activeTimeouts.add(tid);
  }

  async playPadSound(padNumber, options = {}) {
    const startGen = this.generation;
    const padKey = String(padNumber);
    const isAmbient = padKey === String(this.ambientSoundPad);

    // Call /play_pad backend API once per play request
    fetch(`${API_URL}/play_pad`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'access-control-allow-origin': '*',
       },
      body: JSON.stringify({ pad: padNumber }),
    }).catch(err => { });

    // 1. ALWAYS start fetching/getting the buffer immediately, even if muted or loading.
    // This ensures the network request happens ASAP.
    const shouldBypassCache = options.bypassCache === true;
    const audioBuffer = await this._getAudioBuffer(padNumber, { bypassCache: shouldBypassCache });

    // 2. Now check if we should actually PLAY it.
    if (this.isMuted || this.isLoadingScreen || this.generation !== startGen) return null;

    const isRunning = await this._ensureContextRunning();
    if (!isRunning || this.generation !== startGen) {
      if (isAmbient && this.generation === startGen) {
        console.log('[AudioService] Context not running. Queueing ambient sound play.');
        this._pendingPlay = { type: 'ambient', volume: options.volume || 1.0, fadeInDuration: options.fadeInDuration || 3000 };
      } else if (this.generation === startGen) {
        console.log('[AudioService] Context not running. Queueing pad sound play:', padKey);
        this._pendingPlay = { type: 'pad', padNumber: padKey, options };
      }
      return null;
    }

    const settings = { loop: false, volume: 1.0, fadeIn: true, fadeInDuration: 1000, ...options };

    try {
      const existing = isAmbient ? this.ambientAudioSource : this.activeSources[padKey];
      if (existing) {
        if (existing.pendingStopTimeout) {
          clearTimeout(existing.pendingStopTimeout);
          existing.pendingStopTimeout = null;
        }
        if (existing.source) {
          try { existing.source.stop(0); } catch (e) { }
        }
        if (isAmbient) { this.ambientAudioSource = null; } else { delete this.activeSources[padKey]; }
      }

      if (!audioBuffer) return null;

      const { source, gain } = this._createAudioSource(audioBuffer, settings.volume, settings.fadeIn, settings.fadeInDuration);
      if (!source) return null;

      const ctrl = { source, gain, settings, audioBuffer, padNumber: padKey, stopped: false, pendingStopTimeout: null };
      if (isAmbient) { this.ambientAudioSource = ctrl; } else { this.activeSources[padKey] = ctrl; }

      source.onended = () => {
        if (this.activeSources[padKey] === ctrl) {
          delete this.activeSources[padKey];
        }
      };

      if (settings.loop) {
        const soundInfo = this._soundData.find(item => String(item.Number) === String(padNumber));
        if (soundInfo?.overlapDuration) {
          this._scheduleOverlappingLoop(ctrl, startGen);
        } else {
          source.loop = true;
        }
      }

      return { stop: (d, delay) => this.stopPadSound(padNumber, d, delay), padNumber: padKey };
    } catch (e) {
      console.error('[AudioService] Error playing pad', padNumber, e);
      return null;
    }
  }

  async playElementSound(elementId, options = {}) {
    const rawKey = String(elementId || '').trim().toLowerCase();
    const normalizedKey = this._normalizeElementId(elementId);
    const fallbackKey = rawKey.replace(/[^a-z0-9]+/g, '');
    let pad = this.elementToPadMapping[normalizedKey]
      || this.elementToPadMapping[rawKey]
      || this.elementToPadMapping[fallbackKey];

    if (!pad && rawKey === 'crude') {
      pad = this.elementToPadMapping['crudeoil'] || this.elementToPadMapping['crudeoilgrease'];
    }

    if (!pad) return null;
    return this.playPadSound(pad, options);
  }

  async _getAudioBuffer(padNumber, options = {}) {
    const { bypassCache = false } = options;

    if (!bypassCache && this.cachedBuffers[padNumber]) {
      return this.cachedBuffers[padNumber];
    }

    try {
      const res = await fetch(`/sounds/${padNumber}.mp3`);
      if (!res.ok) {
        console.warn(`[AudioService] Failed to fetch sound: /sounds/${padNumber}.mp3`);
        return null;
      }
      const arrayBuffer = await res.arrayBuffer();

      // Now ensure we have a context to decode it
      if (!this.audioContext) {
        this._initContext();
      }

      if (!this.audioContext) return null;

      const buf = await this.audioContext.decodeAudioData(arrayBuffer);
      if (!bypassCache) {
        this.cachedBuffers[padNumber] = buf;
      }
      return buf;
    } catch (e) {
      console.error(`[AudioService] Error loading sound ${padNumber}:`, e);
      return null;
    }
  }

  async stopPadSound(padNumber, fadeOutDuration = 2000, delay = 0) {
    const padKey = String(padNumber);
    const isAmbient = padKey === String(this.ambientSoundPad);
    const ctrl = isAmbient ? this.ambientAudioSource : this.activeSources[padKey];

    if (!ctrl) return;

    if (delay > 0) {
      if (ctrl.pendingStopTimeout) clearTimeout(ctrl.pendingStopTimeout);
      ctrl.pendingStopTimeout = setTimeout(() => {
        ctrl.pendingStopTimeout = null;
        this.stopPadSound(padNumber, fadeOutDuration, 0);
      }, delay);
      return;
    }

    if (isAmbient) { this.ambientAudioSource = null; } else { delete this.activeSources[padKey]; }

    ctrl.stopped = true;
    if (ctrl.source && ctrl.gain && this.audioContext) {
      const { source, gain } = ctrl;
      const now = this.audioContext.currentTime;
      try {
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(0.0, now + fadeOutDuration / 1000);
        await new Promise(resolve => {
          const tid = setTimeout(() => { try { source.stop(0); } catch (e) { } this.activeTimeouts.delete(tid); resolve(); }, fadeOutDuration + 50);
          this.activeTimeouts.add(tid);
        });
      } catch (e) { }
    }
  }

  async stopAllSounds(fadeOutDuration = 2000) {
    this.generation++;
    const promises = Object.keys(this.activeSources).map(p => this.stopPadSound(p, fadeOutDuration));
    if (this.ambientAudioSource) promises.push(this.stopPadSound(this.ambientSoundPad, fadeOutDuration));
    await Promise.all(promises);
    this.currentlyPlayingExclusiveLoop = null;
  }

  async setMuted(muted) {
    this.isMuted = muted;
    if (this.masterGain && this.audioContext) {
      const now = this.audioContext.currentTime;
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.exponentialRampToValueAtTime(muted ? 0.0001 : 1.0, now + 0.1);
    }
    if (muted) {
      await this.stopAllSounds(500);
    } else {
      if (this.audioContext) this.audioContext.resume().catch(() => { });
    }
  }

  setLoadingScreen(active) {
    this.isLoadingScreen = active;
    if (active) {
      this.generation++;
      this.pendingAmbientPlay = false;
      this.stopAllSounds(200).catch(() => { });
    }
  }

  async playAmbientSound(volume = 1.0, fadeInDuration = 3000) {
    const startGen = this.generation;
    if (this.isMuted || this.isLoadingScreen) return null;

    this.pendingAmbientPlay = true;

    if (this.ambientAudioSource) return this.ambientAudioSource;

    // this._pendingPlay = { type: 'ambient', volume, fadeInDuration };

    const nonAmbientKeys = Object.keys(this.activeSources);
    if (nonAmbientKeys.length > 0) {
      await Promise.all(nonAmbientKeys.map(p => this.stopPadSound(p, 300)));
    }

    if (this.generation !== startGen) return null;

    const result = await this.playPadSound(this.ambientSoundPad, { loop: true, volume, fadeInDuration });
    // if (!result && this.audioContext && this.audioContext.state === 'suspended') {
    //   // Keep pending ambient play until the context is resumed via gesture.
    //   return null;
    // }

    if (!this.pendingAmbientPlay || this.generation !== startGen) {
      if (result) this.stopAmbientSound(0);
      return null;
    }
    this._pendingPlay = null;
    return result;
  }

  async stopAmbientSound(fadeOutDuration = 3000) {
    this.pendingAmbientPlay = false;
    await this.stopPadSound(this.ambientSoundPad, fadeOutDuration);
  }

  stopElementSound(elementId, fadeOutDuration = 3000, delay = 0) {
    const key = this._normalizeElementId(elementId);
    const pad = this.elementToPadMapping[key];
    if (!pad) return Promise.resolve();
    return this.stopPadSound(pad, fadeOutDuration, delay);
  }

  preloadCommonSounds() {
    if (!this._soundData?.length || !this.audioContext) return;
    const pads = this._soundData.map(i => i.Number).filter(Boolean);
    let idx = 0;
    const next = async () => { if (idx >= pads.length) return; try { await this._getAudioBuffer(pads[idx]); } catch (e) { } idx++; setTimeout(next, 100); };
    next();
  }

  dispose() {
    this.generation++;
    Object.values(this.activeSources).forEach(s => { try { s?.source?.stop(0); } catch (e) { } });
    try { this.ambientAudioSource?.source?.stop(0); } catch (e) { }
    this.activeSources = {};
    this.ambientAudioSource = null;
    this.cachedBuffers = {};
  }

  resetAllAudioState() {
    this.stopAllSounds(500);
    this.activeTimeouts.forEach(id => clearTimeout(id));
    this.activeTimeouts.clear();
    this.currentlyPlayingExclusiveLoop = null;
    this.dispose();
  }
}

const audioService = new AudioService();
export default audioService;
